import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/apiClient";
import { useAccount } from "@/lib/AccountContext";
import {
  accountDisplayName,
  demographicsFromProfile,
  profileFormValues,
  profilePatchFromDemographics,
  profilePayload,
} from "@/lib/accountProfile";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Demographics() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session") || localStorage.getItem(`se_session_${slug}`);
  const { t, lang } = useLang();
  const [inst, setInst] = useState(null);
  const [form, setForm] = useState({});
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { user, updateProfile } = useAccount();
  const [prefilledKeys, setPrefilledKeys] = useState([]);
  const [saveToProfile, setSaveToProfile] = useState(true);
  // Who is answering: one account may be used to test someone else, so the
  // profile is only used after the respondent confirms it is themselves.
  const [respondent, setRespondent] = useState(null);

  useEffect(() => {
    api.get(`/public/instruments/${slug}`).then(r => setInst(r.data));
  }, [slug]);

  const fields = (inst?.demographics_config || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const anyRequired = fields.some((f) => f.required);
  const askRespondent = Boolean(user) && fields.length > 0;
  const chooseRespondent = (who) => {
    setRespondent(who);
    const answers = who === "self" ? demographicsFromProfile(fields, user.profile) : {};
    setPrefilledKeys(Object.keys(answers));
    setForm(answers);
  };

  const profilePatch = respondent === "self" ? profilePatchFromDemographics(fields, form, user.profile) : {};
  const canSaveToProfile = Object.keys(profilePatch).length > 0;

  const validate = () => {
    for (const f of fields) {
      if (f.required && !form[f.key]) {
        return f;
      }
    }
    return null;
  };

  const submit = async (skip = false) => {
    if (!skip) {
      const missing = validate();
      if (missing) { alert(`${lang === "id" ? "Wajib diisi" : "Required"}: ${missing[`label_${lang}`] || missing.label_id}`); return; }
    }
    setSubmitting(true);
    try {
      if (!skip && canSaveToProfile && saveToProfile) {
        // Saving to the profile is a convenience; never block the instrument on it.
        await updateProfile({ ...profilePayload(profileFormValues(user.profile)), ...profilePatch }).catch(() => {});
      }
      await api.post(`/public/sessions/${sessionId}/demographics`, { data: skip ? {} : form });
      nav(`/instruments/${slug}/questionnaire?session=${sessionId}`);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <h1 className="font-display text-3xl md:text-4xl tracking-tight" data-testid="demo-title">{t(anyRequired ? "demo.title_required" : "demo.title")}</h1>
      <p className="text-muted-foreground mt-2 whitespace-pre-line">{t(anyRequired ? "demo.subtitle_required" : "demo.subtitle")}</p>
      <div className="mt-8 p-6 rounded-2xl bg-card border border-border space-y-5">
        {fields.length > 0 && (
          <div className="text-xs text-muted-foreground p-3 rounded-xl bg-cream-100/80 border border-border" data-testid="demo-personalization-note">
            {anyRequired
              ? (lang === "id"
                ? "Kalau diisi, data ini dipakai untuk menyesuaikan laporan lengkapmu."
                : "If you fill these in, we'll use them to personalise your full report.")
              : lang === "id"
              ? "Semua kolom di halaman ini opsional. Kalau diisi, data ini dipakai untuk menyesuaikan laporan lengkapmu (nama, avatar, dan lainnya). Hasil ringkasnya tetap anonim."
              : "Everything here is optional. If you fill it in, we'll use it to personalise your full report (name, avatar, and so on). Your summary result stays anonymous."}
          </div>
        )}
        {askRespondent && (
          <div className="space-y-3" data-testid="demo-respondent">
            <div className="text-base font-display text-teal-800">
              {lang === "id" ? "Siapa yang mengisi tes ini?" : "Who is taking this test?"}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ["self", lang === "id" ? `Saya sendiri (${accountDisplayName(user)})` : `Me (${accountDisplayName(user)})`],
                ["other", lang === "id" ? "Orang lain" : "Someone else"],
              ].map(([who, label]) => (
                <Button
                  key={who}
                  type="button"
                  variant="outline"
                  aria-pressed={respondent === who}
                  onClick={() => chooseRespondent(who)}
                  className={`h-auto min-h-11 whitespace-normal rounded-xl text-left justify-start ${respondent === who ? "border-teal-700 bg-teal-50 text-teal-900" : ""}`}
                  data-testid={`demo-respondent-${who}`}
                >
                  {label}
                </Button>
              ))}
            </div>
            {respondent === "other" && (
              <p className="text-xs text-muted-foreground" data-testid="demo-respondent-other-note">
                {lang === "id"
                  ? "Data profil akun tidak dipakai. Isi data orang yang mengerjakan tes ini."
                  : "Your account profile is not used. Enter the details of the person taking this test."}
              </p>
            )}
          </div>
        )}
        {askRespondent && !respondent && !anyRequired && (
          <Button data-testid="demo-skip-btn" variant="outline" disabled={submitting} onClick={() => submit(true)} className="w-full h-11 rounded-full">
            {t("demo.skip")}
          </Button>
        )}
        {(!askRespondent || respondent) && (
          <>
          {prefilledKeys.length > 0 && (
            <div className="text-xs text-teal-800 p-3 rounded-xl bg-sage-50 border border-border" data-testid="demo-prefilled-note">
              {lang === "id"
                ? "Sebagian jawaban sudah terisi otomatis dari profil akunmu. Kamu tetap bisa mengubahnya."
                : "Some answers were filled in from your account profile. You can still change them."}
            </div>
          )}
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : fields.map((f) => {
            const label = f[`label_${lang}`] || f.label_id;
            const desc = f[`description_${lang}`] || f.description_id;
            return (
            <div key={f.key} className="space-y-1.5" data-testid={`demo-field-${f.key}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-base font-display text-teal-800">
                  {label}
                </Label>
                {f.required ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-terracotta-100 text-terracotta-700 uppercase tracking-widest">
                    {lang === "id" ? "Wajib" : "Required"}
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground uppercase tracking-widest" data-testid={`demo-optional-${f.key}`}>
                    {lang === "id" ? "Opsional" : "Optional"}
                  </span>
                )}
              </div>
              {desc && (
                <p className="text-xs text-muted-foreground leading-relaxed break-words" data-testid={`demo-desc-${f.key}`}>{desc}</p>
              )}
              {f.type === "select" ? (
                <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                  <SelectTrigger data-testid={`demo-${f.key}`} className="h-11"><SelectValue placeholder={lang === "id" ? "Pilih…" : "Select…"} /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((o) => (<SelectItem key={o.value} value={o.value}>{o[`label_${lang}`] || o.label_id}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid={`demo-${f.key}`}
                  type={f.type === "number" ? "number" : (f.type === "email" ? "email" : "text")}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="h-11"
                  placeholder={f[`placeholder_${lang}`] || f.placeholder_id || ""}
                />
              )}
            </div>
            );
          })}
          {canSaveToProfile && (
            <label className="flex items-start gap-3 text-sm text-muted-foreground" data-testid="demo-save-profile">
              <Checkbox checked={saveToProfile} onCheckedChange={(v) => setSaveToProfile(v === true)} className="mt-0.5" />
              <span>{lang === "id" ? "Simpan jawaban ini ke profil akunku untuk tes berikutnya." : "Save these answers to my profile for future tests."}</span>
            </label>
          )}
          <div className="flex items-center gap-3 pt-2">
            <Button data-testid="demo-continue-btn" disabled={submitting} onClick={() => submit(false)} className="flex-1 h-11 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
              {t("demo.continue")}
            </Button>
            {fields.every(f => !f.required) && (
              <Button data-testid="demo-skip-btn" variant="outline" onClick={() => submit(true)} className="h-11 rounded-full">
                {t("demo.skip")}
              </Button>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
